import { Button, Popconfirm, Spin, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { Moment } from 'moment'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TABLE_MAX_HEIGHT } from '@/constants/customer'
import {
  GetWashingHistoryByDayResponse,
  WashingHistory,
} from '@/types/apis/washingHistory/GetWashingHistoryByDayResponse'
import { formatDateSendRequest, formatHm, formatMinutes, formatYMD } from '@/utils/date'
import { formatJapaneseNumber } from '@/utils/formatPrice'

import { CustomRowTable } from '../styles'

type Props = {
  washingHistoryByDayResponse: GetWashingHistoryByDayResponse | undefined
  washingHistoryByDayResponseLoading: boolean
  dateFilter: Moment | null
  deleteLaundry: (id: number) => Promise<void>
  deleteLoading: boolean
  setIsReloadByDate: React.Dispatch<React.SetStateAction<boolean>>
}

const WashingHistoryByDayTable = ({
  washingHistoryByDayResponse,
  washingHistoryByDayResponseLoading,
  dateFilter,
  deleteLaundry,
  setIsReloadByDate,
}: Props) => {
  const { t } = useTranslation()
  const [visibleConfirmDelete, setVisibleConfirmDelete] = useState<Record<string, boolean>>({})

  const onDelete = async (id: number) => {
    setIsReloadByDate(true)
    await deleteLaundry(id)
    setVisibleConfirmDelete({})
    setIsReloadByDate(false)
  }

  const baseColumns: ColumnsType<WashingHistory> = [
    {
      title: t('components.pages.washingHistory.listReport.table.date'),
      dataIndex: 'date',
      key: 'date',
      width: 100,
      fixed: 'left',
      align: 'center',
      className: 'nowrap-tank',
      render: () => {
        return <>{formatYMD(formatDateSendRequest(dateFilter))}</>
      },
    },
    {
      title: t('components.pages.washingHistory.listReport.table.customerCode'),
      dataIndex: 'customerCode',
      key: 'customerCode',
      width: 120,
      fixed: 'left',
      align: 'center',
      className: 'nowrap-tank',
    },
    {
      title: t('components.pages.washingHistory.listReport.table.customerName'),
      dataIndex: 'customerShortName',
      key: 'customerShortName',
      width: 200,
      fixed: 'left',
      align: 'center',
      className: 'nowrap-tank',
      render: (customerShortName: string) => {
        return <CustomRowTable type="left">{customerShortName}</CustomRowTable>
      },
    },
    {
      title: t('components.pages.washingHistory.listReport.table.washingMachineName'),
      dataIndex: 'washingMachineName',
      key: 'washingMachineName',
      width: 150,
      fixed: 'left',
      align: 'center',
      className: 'nowrap-tank',
      render: (text: string) => {
        return <CustomRowTable type="left">{text}</CustomRowTable>
      },
    },
    {
      title: t('components.pages.washingHistory.listReport.table.startDateTime'),
      dataIndex: 'laundryStart',
      key: 'laundryStart',
      align: 'center',
      className: 'nowrap-tank',
      render: (laundryStart: string) => {
        return <CustomRowTable type="left">{formatHm(laundryStart)}</CustomRowTable>
      },
    },
    {
      title: t('components.pages.washingHistory.listReport.table.endDateTime'),
      dataIndex: 'finishedTime',
      key: 'finishedTime',
      align: 'center',
      className: 'nowrap-tank',
      render: (finishedTime: string) => {
        return <CustomRowTable type="left">{formatHm(finishedTime)}</CustomRowTable>
      },
    },
    {
      title: t('components.pages.washingHistory.listReport.table.washingTime'),
      dataIndex: 'washingTime',
      key: 'washingTime',
      align: 'center',
      className: 'nowrap-tank',
      render: (washingTime: number) => {
        return <CustomRowTable type="left">{formatMinutes(washingTime)}</CustomRowTable>
      },
    },
    {
      title: t('components.pages.washingHistory.listReport.table.washingMethod'),
      dataIndex: 'washingMethodName',
      key: 'washingMethodName',
      align: 'center',
      className: 'nowrap-tank',
      render: (washingMethodName: string) => {
        return <CustomRowTable type="left">{washingMethodName}</CustomRowTable>
      },
    },
    {
      title: t('components.pages.washingHistory.listReport.table.laundryWeight'),
      dataIndex: 'volumes',
      key: 'volumes',
      align: 'center',
      className: 'nowrap-tank',
      render: (volumes: number) => {
        return <CustomRowTable type="right">{volumes}</CustomRowTable>
      },
    },
    {
      title: t('components.pages.washingHistory.listReport.table.detergentCost'),
      dataIndex: 'cost',
      key: 'cost',
      align: 'center',
      className: 'nowrap-tank',
      render: (cost: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(cost)}</CustomRowTable>
      },
    },
  ]

  const detergentColumns: ColumnsType<WashingHistory> =
    washingHistoryByDayResponse?.detergent.map((item) => ({
      title: item.name + '/ml',
      dataIndex: item.name,
      key: item.name,
      align: 'center',
      className: 'nowrap-export',
      render: (_: GetWashingHistoryByDayResponse, record: WashingHistory) => {
        const found = record.laundryDetergents.find((det) => det.name === item.name)
        return (
          <CustomRowTable type="right">
            {found && found.quantity ? formatJapaneseNumber(found.quantity) : ''}
          </CustomRowTable>
        )
      },
    })) || []

  const waterColumn: ColumnsType<WashingHistory> = [
    {
      title: t('components.pages.washingHistory.listReport.table.water'),
      dataIndex: 'waterQuantity',
      key: 'waterQuantity',
      align: 'center',
      className: 'nowrap-export',
      render: (waterQuantity: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(waterQuantity)}</CustomRowTable>
      },
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      className: 'nowrap',
      render: (text: string, record: WashingHistory) => {
        return (
          <Popconfirm
            id={record.id.toString()}
            visible={!!visibleConfirmDelete[record.id]}
            title={t('components.pages.attendanceLogs.list.table.confirmDelete')}
            okText={t('common.yes')}
            cancelText={t('common.no')}
            onConfirm={() => {
              onDelete(record.id)
            }}
            onCancel={() => setVisibleConfirmDelete({})}
          >
            <Button size="small" danger onClick={() => setVisibleConfirmDelete({ [record.id]: true })} loading={false}>
              {t('components.pages.admins.list.table.delete')}
            </Button>
          </Popconfirm>
        )
      },
    },
  ]

  const finalColumns = [...baseColumns, ...detergentColumns, ...waterColumn]

  const summaryDataBase = [
    {
      key: 1,
      title: t('components.pages.washingHistory.listReport.table.totalUsage'),
    },
    {
      key: 2,
      title: t('components.pages.washingHistory.listReport.table.usagePerCapacity'),
    },
    {
      key: 3,
      title: t('components.pages.washingHistory.listReport.table.amount'),
    },
  ]

  const firstItemExtended = washingHistoryByDayResponse?.detergent.reduce((acc, item) => {
    const key = `${item.name}`
    return {
      ...acc,
      [key]: item.totalQuantity ? formatJapaneseNumber(item.totalQuantity) : '',
      ['waterQuantity']: washingHistoryByDayResponse.totalWaterQuantity
        ? formatJapaneseNumber(washingHistoryByDayResponse.totalWaterQuantity)
        : '',
    }
  }, summaryDataBase[0])

  const secondItemExtended = washingHistoryByDayResponse?.detergent.reduce((acc, item) => {
    const key = `${item.name}`
    return {
      ...acc,
      [key]: item.totalSpecificGravity ? formatJapaneseNumber(item.totalSpecificGravity) : '',
      ['waterQuantity']: washingHistoryByDayResponse.totalWaterSpecificGravity
        ? formatJapaneseNumber(washingHistoryByDayResponse.totalWaterSpecificGravity)
        : '',
    }
  }, summaryDataBase[1])

  const thirdItemExtended = washingHistoryByDayResponse?.detergent.reduce((acc, item) => {
    const key = `${item.name}`
    return {
      ...acc,
      [key]: item.totalPrice ? formatJapaneseNumber(item.totalPrice) : '',
      ['cost']: washingHistoryByDayResponse?.totalDetergentPrice
        ? formatJapaneseNumber(washingHistoryByDayResponse?.totalDetergentPrice)
        : '',
      ['waterQuantity']: washingHistoryByDayResponse.totalWaterPrice
        ? formatJapaneseNumber(washingHistoryByDayResponse.totalWaterPrice)
        : '',
    }
  }, summaryDataBase[2])

  const summaryData = [firstItemExtended, secondItemExtended, thirdItemExtended]

  return (
    <Spin spinning={washingHistoryByDayResponseLoading}>
      <Table
        columns={finalColumns}
        dataSource={washingHistoryByDayResponse?.items}
        bordered
        pagination={false}
        scroll={{ x: 'max-content', y: TABLE_MAX_HEIGHT }}
        summary={() => (
          <>
            {summaryData.map((row) => (
              <Table.Summary.Row key={row?.key}>
                {finalColumns.map((col, idx) => {
                  if (idx === 3) {
                    return (
                      <Table.Summary.Cell key={col.key} index={idx}>
                        <strong>{row?.title}</strong>
                      </Table.Summary.Cell>
                    )
                  }

                  const value = row ? row[col.key as keyof typeof row] : ''
                  return (
                    <Table.Summary.Cell key={col.key} index={idx}>
                      <CustomRowTable type="right">{value}</CustomRowTable>
                    </Table.Summary.Cell>
                  )
                })}
              </Table.Summary.Row>
            ))}
          </>
        )}
      />
    </Spin>
  )
}

WashingHistoryByDayTable.displayName = 'WashingHistoryByDayTable'
export default WashingHistoryByDayTable
